/**
 * Emit hosts for the `context-files` analytics group — 6 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';

export function deleteContextFiles(filesAmount: number) {
  trackLegacyEvent({
    name: SegmentObjectName.ContextFiles,
    props: {
      action: SegmentActionName.ContextFilesDeleted,
      numberFilesDeleted: filesAmount,
    },
  });

  // V2 — Amplitude migration
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ContextDeleted,
    props: {
      file_count: filesAmount,
    },
  });
}

export function saveContextFilesUpdates() {
  trackLegacyEvent({
    name: SegmentObjectName.ContextFiles,
    props: {
      action: SegmentActionName.ContextFilesUpdatesSaved,
    },
  });

  // V2 — Amplitude migration
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ContextUpdated,
    props: {},
  });
}

export function uploadContextFileFailed(fileSize: number) {
  trackLegacyEvent({
    name: SegmentObjectName.ContextFiles,
    props: {
      action: SegmentActionName.ContextFileUploadFailed,
      fileSize,
    },
  });

  // V2 — Amplitude migration (consolidate to context_failed)
  track({
    event_name: TrackingEventName.ContextFailed,
    props: {
      file_size: fileSize,
    },
  });
}

export function uploadContextFileSuccess(fileSize: number) {
  trackLegacyEvent({
    name: SegmentObjectName.ContextFiles,
    props: {
      action: SegmentActionName.ContextFileUploadSuccess,
      fileSize,
    },
  });

  // V2 — Amplitude migration (consolidate to context_uploaded)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ContextUploaded,
    props: {
      upload_action: 'file_upload_success',
      file_size: fileSize,
    },
  });
}

export function uploadContextFiles(fileSize: number) {
  trackLegacyEvent({
    name: SegmentObjectName.ContextFiles,
    props: {
      action: SegmentActionName.ContextFilesUploaded,
      fileSize,
    },
  });

  // V2 — Amplitude migration
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ContextUploaded,
    props: {
      upload_action: 'files_uploaded',
      file_size: fileSize,
    },
  });
}

export function uploadContextSingleFile(fileSize: number) {
  trackLegacyEvent({
    name: SegmentObjectName.ContextFiles,
    props: {
      action: SegmentActionName.ContextFileUploaded,
      fileSize,
    },
  });

  // V2 — Amplitude migration (consolidate to context_uploaded)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ContextUploaded,
    props: {
      upload_action: 'file_uploaded',
      file_size: fileSize,
    },
  });
}
